import { z } from 'zod';
import { jsonObjectSchema } from './json.ts';

export const safeResourceUrlSchema = z.string().min(1).max(5000).refine(
  (value) => {
    if (/^\/(?!\/)/.test(value))
      return true;
    try {
      return ['http:', 'https:', 'blob:'].includes(new URL(value).protocol);
    }
    catch {
      return false;
    }
  },
  '资源地址只允许站内绝对路径或 http、https、blob 协议',
);

export const builtinArtifactKindSchema = z.enum([
  'image',
  'video',
  'lottie',
  'scene-spec',
  'chart-spec',
]);

export const artifactKindSchema = z.union([
  builtinArtifactKindSchema,
  z.string().regex(
    /^[a-z0-9][a-z0-9-]*(?:\.[a-z0-9][a-z0-9-]*)+@\d+$/,
    '扩展资源类型必须使用 namespace.name@version 格式',
  ),
]);

export const artifactChapterSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  startSeconds: z.number().nonnegative(),
  endSeconds: z.number().nonnegative().optional(),
  description: z.string().optional(),
}).strict().superRefine((chapter, context) => {
  if (
    chapter.endSeconds !== undefined
    && chapter.endSeconds < chapter.startSeconds
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endSeconds'],
      message: '章节结束时间不能早于开始时间',
    });
  }
});

export const artifactCueSchema = z.object({
  id: z.string().min(1),
  timeSeconds: z.number().nonnegative(),
  label: z.string().default(''),
  eventType: z.string().min(1).default('timeline.cue.entered'),
  payload: jsonObjectSchema.default({}),
}).strict();

export const artifactGeneratorSchema = z.object({
  key: z.string().min(1),
  version: z.string().min(1),
  parameters: jsonObjectSchema.default({}),
}).strict();

export const artifactManifestEntrySchema = z.object({
  id: z.string().min(1),
  kind: artifactKindSchema,
  title: z.string().default(''),
  source: z.enum(['catalog', 'generated', 'uploaded']).default('catalog'),
  catalogAssetKey: z.string().regex(
    /^[a-z0-9][a-z0-9-]*(?:\.[a-z0-9][a-z0-9-]*)+@\d+$/,
  ).optional(),
  status: z.enum(['pending', 'processing', 'ready', 'failed']).default('ready'),
  url: safeResourceUrlSchema.optional(),
  mimeType: z.string().min(1).optional(),
  posterUrl: safeResourceUrlSchema.optional(),
  durationSeconds: z.number().nonnegative().optional(),
  captionsUrl: safeResourceUrlSchema.optional(),
  chapters: z.array(artifactChapterSchema).max(1000).default([]),
  cues: z.array(artifactCueSchema).max(5000).default([]),
  generator: artifactGeneratorSchema.optional(),
  metadata: jsonObjectSchema.default({}),
  checksum: z.string().optional(),
}).strict().superRefine((artifact, context) => {
  if (artifact.source !== 'catalog' && artifact.catalogAssetKey) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['catalogAssetKey'],
      message: '只有目录资源可以引用 catalogAssetKey',
    });
  }
  if (
    artifact.status === 'ready'
    && (artifact.kind === 'image' || artifact.kind === 'video')
    && !artifact.url
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['url'],
      message: '已就绪的图片或视频资源必须提供 url',
    });
  }
  for (const [field, items] of [
    ['chapters', artifact.chapters],
    ['cues', artifact.cues],
  ] as const) {
    const ids = new Set<string>();
    for (const [index, item] of items.entries()) {
      if (ids.has(item.id)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field, index, 'id'],
          message: `资源时间项编号重复：${item.id}`,
        });
      }
      ids.add(item.id);
    }
  }
});

export type ArtifactKind = z.infer<typeof artifactKindSchema>;
export type BuiltinArtifactKind = z.infer<typeof builtinArtifactKindSchema>;
export type ArtifactChapter = z.infer<typeof artifactChapterSchema>;
export type ArtifactCue = z.infer<typeof artifactCueSchema>;
export type ArtifactGenerator = z.infer<typeof artifactGeneratorSchema>;
export type ArtifactManifestEntry = z.infer<
  typeof artifactManifestEntrySchema
>;
